import type {
  ApiTokenFormValues,
  PluginColour,
  PluginKeyEnvironment,
  PluginKeyFormValues,
  PluginTab,
  PluginThemeMode,
} from '../types'

export const PLUGIN_KEY_INITIAL_VALUES: PluginKeyFormValues = {
  name: '',
  environment: 'live',
  websites: '',
  allowSandboxed: false,
}

export const API_TOKEN_INITIAL_VALUES: ApiTokenFormValues = {
  name: '',
  canRead: false,
  canWrite: false,
}

export const PLUGIN_KEY_ENVIRONMENT_OPTIONS: {
  label: string
  value: PluginKeyEnvironment
}[] = [
  { label: 'Live', value: 'live' },
  { label: 'Test', value: 'test' },
]

/** API `invalid_fields` key → Website Plugin key form field. */
export const PLUGIN_KEY_FIELD_MAP: Record<string, string> = {
  name: 'name',
  environment: 'environment',
  allowed_origins: 'websites',
  allow_sandboxed: 'allowSandboxed',
}

export const PLUGIN_KEY_FIELD_TITLES: Record<string, string> = {
  name: 'Name',
  environment: 'Environment',
  allowed_origins: 'Hosted Websites',
  allow_sandboxed: 'Hidden Website Addresses',
}

/** A refused ability (`abilities.N`, collapsed to `abilities`) shows under the Read switch. */
export const API_TOKEN_FIELD_MAP: Record<string, string> = {
  name: 'name',
  abilities: 'canRead',
}

export const API_TOKEN_FIELD_TITLES: Record<string, string> = {
  name: 'Name',
  abilities: 'Access',
}

/** Success copy, shared by web (one-line toast) and any future mobile screen. */
export const API_ACCESS_SUCCESS_MESSAGES = {
  pluginKeyCreated: {
    title: 'Website Plugin Key Created',
    subtitle: 'Your Website Plugin key is ready to add to your website.',
  },
  pluginKeyUpdated: {
    title: 'Website Plugin Key Updated',
    subtitle: 'Your Website Plugin key has been updated.',
  },
  pluginKeyRevoked: {
    title: 'Website Plugin Key Revoked',
    subtitle: 'The Website Plugin key no longer works on any website.',
  },
  apiTokenCreated: {
    title: 'API Token Created',
    subtitle: 'Copy your API token now. It will not be shown again.',
  },
  apiTokenDeleted: {
    title: 'API Token Deleted',
    subtitle: 'The API token no longer works.',
  },
  exampleRequestCopied: {
    title: 'Example Request Copied',
    subtitle: 'Swap in your API token, then run it in a terminal.',
  },
  embedCodeCopied: {
    title: 'Embed Code Copied',
    subtitle: 'Embed code copied. Paste it into your website.',
  },
  pluginConfigSaved: {
    title: 'Website Plugin Settings Saved',
    subtitle: 'Your website shows the new settings the next time it loads.',
  },
}

/** In the order the plugin shows them. */
export const PLUGIN_TAB_OPTIONS: { label: string; value: PluginTab }[] = [
  { label: 'Schedule', value: 'schedule' },
  { label: 'Standings', value: 'standings' },
  { label: 'Statistics', value: 'statistics' },
  { label: 'Suspensions', value: 'suspensions' },
  { label: 'Announcements', value: 'announcements' },
  { label: 'Registrations', value: 'registrations' },
]

export const PLUGIN_COLOUR_OPTIONS: { label: string; value: PluginColour }[] = [
  { label: 'Background', value: 'background' },
  { label: 'Text', value: 'text' },
  { label: 'Header Background', value: 'headerBackground' },
  { label: 'Header Text', value: 'headerText' },
  { label: 'Primary', value: 'primary' },
  { label: 'Border', value: 'border' },
]

export const PLUGIN_THEME_MODE_OPTIONS: {
  label: string
  value: PluginThemeMode
}[] = [
  { label: 'Auto', value: 'auto' },
  { label: 'Light', value: 'light' },
  { label: 'Dark', value: 'dark' },
]

/**
 * Below this (WCAG non-text contrast), the plugin draws tab underlines in the
 * header text colour instead of the primary colour.
 */
export const PLUGIN_MIN_HEADER_ACCENT_CONTRAST = 3

/** The plugin v2 loader every embed snippet loads. */
export const PLUGIN_EMBED_SCRIPT_URL = 'https://embed.sportninja.com/embed.js'

/**
 * How to add the plugin to a website. The same page the plugin itself links to
 * (sn-plugin2 `apps/embed/src/configs/support.ts`); Canlan has none of its own yet.
 */
export const PLUGIN_SETUP_GUIDE_URL =
  'https://support.sportninja.com/website-widget-web-plugin-integration-guide-new'

/** API `invalid_fields` key → Website Plugin settings form field. */
export const PLUGIN_CONFIG_FIELD_MAP: Record<string, string> = {
  initial_tab: 'initialTab',
  tabs: 'tabs',
  'theme.mode': 'mode',
  ...Object.fromEntries(
    PLUGIN_TAB_OPTIONS.map(({ value }) => [`tabs.${value}`, value])
  ),
  ...Object.fromEntries(
    PLUGIN_COLOUR_OPTIONS.map(({ value }) => [`theme.${value}`, value])
  ),
}

export const PLUGIN_CONFIG_FIELD_TITLES: Record<string, string> = {
  initial_tab: 'Opening Tab',
  tabs: 'Tabs',
  'theme.mode': 'Mode',
  ...Object.fromEntries(
    PLUGIN_TAB_OPTIONS.map(({ label, value }) => [`tabs.${value}`, label])
  ),
  ...Object.fromEntries(
    PLUGIN_COLOUR_OPTIONS.map(({ label, value }) => [`theme.${value}`, label])
  ),
}
